import { Info } from '@mui/icons-material';
import { Box, styled, TooltipProps, Typography } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import { LightTooltip } from '../WhiteTooltip';

interface SubtitleWithTooltipProps {
  title: string;
  tooltipContent: React.ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
  children?: React.ReactNode;
  variant?: 'default' | 'compact';
}

const StyledContainer = styled(Box)<{ variant: string }>(({ variant }) => {
  const baseStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.3s ease-in-out',
  };

  switch (variant) {
    case 'compact':
      return {
        ...baseStyle,
        marginBottom: '8px',
        borderRadius: '8px',
      };
    default:
      return {
        ...baseStyle,
        padding: '16px 0px',
        marginBottom: '16px',
      };
  }
});

const StyledTitle = styled(Typography)<{
  color: string;
  secondaryColor: string;
}>(({ color, secondaryColor }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  color: '#1a202c',
  letterSpacing: '0.01em',
  position: 'relative',
  paddingLeft: '12px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '3px',
    height: '20px',
    background: `linear-gradient(135deg, ${color} 0%, ${secondaryColor} 100%)`,
    borderRadius: '2px',
  },
}));

const StyledIcon = styled(Info)<{ mainColor: string; secondaryColor: string }>(
  ({ mainColor, secondaryColor }) => ({
    fontSize: 20,
    color: '#64748b',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    padding: '4px',
    borderRadius: '50%',
    '&:hover': {
      color: mainColor,
      backgroundColor: `${secondaryColor}50`,
      transform: 'scale(1.1)',
    },
  }),
);

const SubtitleWithTooltip: React.FC<SubtitleWithTooltipProps> = ({
  title,
  tooltipContent,
  tooltipPlacement = 'right',
  children,
  variant = 'default',
}) => {
  const expressConfig = useSelector(
    (state: RootState) => state.clientConfiguration.configs,
  );
  const mainColor =
    expressConfig?.pruffConnect_styles_mainColor ?? BASE_COLORS.blue;
  const secondaryColor =
    expressConfig?.pruffConnect_styles_secondaryColor ?? BASE_COLORS.lightBlue;

  const titleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const childrenContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  return (
    <StyledContainer variant={variant}>
      <Box style={titleContainerStyle}>
        <StyledTitle
          variant="body1"
          color={mainColor}
          secondaryColor={secondaryColor}
        >
          {title}
        </StyledTitle>
        <LightTooltip
          placement={tooltipPlacement}
          title={
            <Box style={{ padding: '12px', maxWidth: '300px' }}>
              <Typography
                variant="body2"
                style={{
                  lineHeight: 1.5,
                  color: '#374151',
                  fontSize: '0.875rem',
                }}
              >
                {tooltipContent}
              </Typography>
            </Box>
          }
        >
          <StyledIcon mainColor={mainColor} secondaryColor={secondaryColor} />
        </LightTooltip>
      </Box>
      {children && <Box style={childrenContainerStyle}>{children}</Box>}
    </StyledContainer>
  );
};

export default SubtitleWithTooltip;
